import { FaWhatsapp, FaInstagram, FaYoutube, FaLinkedin } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { LangLink as Link } from "@/components/LangLink";
import logo from "@/assets/bizu-logo.png";

export function Footer() {
  const { t } = useTranslation("common");

  const companyLinks = [
    { label: t("nav.home", "Home"), to: "/" },
    { label: t("nav.about", "About"), to: "/about" },
    { label: t("nav.missions", "Missions"), to: "/missions" },
    { label: t("nav.careers", "Careers"), to: "/careers" },
  ];

  const productLinks = [
    { label: t("nav.products", "Products"), to: "/products" },
    { label: t("nav.store", "Store"), to: "https://montink.com/bizu-space-store/" },
    { label: t("nav.contact", "Contact"), to: "/contact" },
  ];

  const servicesLinks = [
    { label: t("nav.dropdown.manufacturing", "Aerospace Manufacturing"), to: "/services/manufacturing" },
    { label: t("nav.dropdown.t8", "T8 Test Facility"), to: "/services/t8" },
    { label: t("nav.dropdown.aerospace", "Aerospace and Systems Engineering"), to: "/services/aerospace" },
    { label: t("nav.dropdown.launch", "Launch Services"), to: "/services/ftl" },
  ];

  return (
    <footer className="relative overflow-hidden bg-[#0b0b0b] text-white">
      <div className="relative container-bizu py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 xl:grid-cols-[1.15fr_0.85fr_0.85fr_1.45fr]">
          <div className="space-y-4">
            <img src={logo} alt="BIZU Space" className="h-auto w-[140px] max-w-full" width={400} height={120} />

            <div className="text-[16px] font-medium leading-relaxed text-white/85 inline-flex flex-col items-center gap-3 pt-4">
              <h3 className="text-[22px] font-bold uppercase tracking-wide text-white whitespace-nowrap text-center">
                {t("footer.getInTouch", "GET IN TOUCH")}
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href="https://www.linkedin.com/company/bizu-space"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaLinkedin size={22} />
                </a>

                <a
                  href="https://www.instagram.com/bizu.space"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaInstagram size={22} />
                </a>

                <a
                  href="https://www.youtube.com/@bizuspace"
                  aria-label="YouTube"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaYoutube size={24} />
                </a>

                <a
                  href="https://api.whatsapp.com/send?phone=5512982383191"
                  aria-label="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaWhatsapp size={24} />
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col xl:contents">
            <FooterColumn title={t("footer.company", "Company")} links={companyLinks} />
            <div className="xl:pt-[34px] pt-0">
              <FooterColumn title="" links={productLinks} noTitle />
            </div>
          </div>

          <FooterColumn title={t("footer.services", "Services")} links={servicesLinks} />
        </div>

        <div className="flex flex-col justify-center gap-4 pt-12 text-[15px] font-medium text-white/55 sm:flex-row sm:items-center">
          <span>{t("footer.copyright", "© 2026 BIZU Space - All rights reserved")}</span>
        </div>
      </div>
    </footer >
  );
}

function FooterColumn({
  title,
  links,
  noTitle = false,
}: {
  title?: string;
  links: { label: string; to: string }[];
  noTitle?: boolean;
}) {
  return (
    <div>
      {!noTitle && (
        <h3 className="text-[22px] font-bold uppercase tracking-wide text-white">{title}</h3>
      )}
      <ul>
        {links.map((link) => (
          <li
            key={link.label}
            className="group relative border-b border-white/15 transition-colors duration-300 hover:border-accent"
          >
            {link.to.startsWith("http") ? (
              <a
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex w-full items-center justify-between gap-4 py-4 text-[17px] font-semibold leading-[1.45] text-white transition-all duration-300 hover:text-accent group-hover:translate-y-[-2px]"
              >
                <span>{link.label}</span>

                <span className="flex min-w-[22px] items-center justify-center overflow-hidden">
                  <span className="relative h-5 w-5">
                    <span className="absolute inset-0 transition-all duration-300 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:opacity-0">
                      →
                    </span>
                    <span className="absolute inset-0 translate-x-[-10px] translate-y-[10px] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100">
                      ↗
                    </span>
                  </span>
                </span>
              </a>
            ) : (
              <Link
                to={link.to}
                className="relative flex w-full items-center justify-between gap-4 py-4 text-[17px] font-semibold leading-[1.45] text-white transition-all duration-300 hover:text-accent group-hover:translate-y-[-2px]"
              >
                <span>{link.label}</span>

                <span className="flex min-w-[22px] items-center justify-center overflow-hidden">
                  <span className="relative h-5 w-5">
                    <span className="absolute inset-0 transition-all duration-300 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:opacity-0">
                      →
                    </span>
                    <span className="absolute inset-0 translate-x-[-10px] translate-y-[10px] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100">
                      ↗
                    </span>
                  </span>
                </span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
